import React, { useCallback, useRef, useState } from 'react';

import { Button, Divider, Flex, Icon, Image, InputBase, Text } from '@opendoor/bricks/core';

import { useMutation, useQuery } from 'platform/apollo';

import { LOWER_MORTGAGE_FLOW_URL } from 'shared/helpers/globals';
import { useMobileDevice } from 'shared/helpers/hooks/useMobileDevice';

import { LowerMortgageModal } from 'buying/components/buyDirectCheckout/LowerMortgageModal';
import { CreateCustomerMortgageLead } from 'buying/graphql/mutations';
import {
  CreateCustomerMortgageLead as CreateCustomerMortgageLeadData,
  CreateCustomerMortgageLeadVariables,
  CreateCustomerMortgageLead_customerFinancingService_createCustomerMortgageLead_mortgageLead as CustomerMortgageLead,
} from 'buying/graphql/mutations/__generated__/CreateCustomerMortgageLead';
import { GetCurrentCustomer } from 'buying/graphql/queries';
import { GetCurrentCustomer as GetCurrentCustomerData } from 'buying/graphql/queries/__generated__/GetCurrentCustomer';

const LOWER_FORM_URL = new URL(
  `${LOWER_MORTGAGE_FLOW_URL}/mortgage/lightspeed/partners?partner_name=opendoor&rea_question=false&consent_required=true&show_warm_up=true&loan_type=purchase&utm_content=buy_direct_checkout`,
);

interface IPreApprovalModuleProps {
  onBackClick: () => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Generic PreApproval module
 * storybook-check-ignore
 */
export const PreApprovalModule: React.FC<IPreApprovalModuleProps> = ({
  onBackClick,
  onFileChange,
}) => {
  const [showModal, setShowModal] = useState(false);
  const { isMobile } = useMobileDevice();
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);

  const { data: currentCustomerData } = useQuery<GetCurrentCustomerData>(GetCurrentCustomer, {
    fetchPolicy: 'cache-first',
  });

  const customerUuid = currentCustomerData?.customerService?.getCurrentCustomer?.customer?.uuid;
  const [createCustomerMortgageLead] = useMutation<
    CreateCustomerMortgageLeadData,
    CreateCustomerMortgageLeadVariables
  >(CreateCustomerMortgageLead, {
    onCompleted: (data: CreateCustomerMortgageLeadData) => {
      handleGetPreApprovedWebFormRedirect(
        // @ts-expect-error -- variable is possibly undefined/null
        data?.customerFinancingService?.createCustomerMortgageLead?.mortgageLead,
      );
    },
  });

  const handleGetPreApprovedWebFormRedirect = useCallback(
    (mortgageLead: CustomerMortgageLead) => {
      const mortgageLeadId = mortgageLead?.mortgageLeadId;
      if (mortgageLeadId && customerUuid) {
        LOWER_FORM_URL.searchParams.append(
          'vendor_lead_unique_id',
          `${customerUuid}_${mortgageLeadId}`,
        );
      }
      window.open(LOWER_FORM_URL.toString(), '_blank');
    },
    [customerUuid],
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleOpenModal = useCallback(() => {
    setShowModal(true);
  }, []);

  // The hidden input is used to trigger the file upload modal and needs to be "clicked" programmatically
  const triggerUploadModal = () => hiddenInputRef?.current?.click();

  const onLowerPreApprovalClick = useCallback(async () => {
    await createCustomerMortgageLead({
      variables: {
        // @ts-expect-error -- variable is possibly undefined/null
        customerUuid,
      },
    });
    handleCloseModal();
  }, [createCustomerMortgageLead, customerUuid, handleCloseModal]);

  return (
    <>
      <Flex flexDirection="column" width="100%">
        <Text alignSelf="flex-start" mt={5} size="110" color="neutrals90" fontWeight="medium">
          Upload
        </Text>
        <Flex flexDirection="column" width="100%">
          <Flex flexDirection="row" justifyContent="space-between" alignItems="center" mt={6}>
            <Button
              analyticsName="buy-direct-share-pre-approval"
              size="lg"
              variant="ghost"
              onClick={triggerUploadModal}
              aria-label="share-pre-approval-text"
            >
              <Text size="150" fontWeight="medium">
                Share your pre-approval letter
              </Text>
            </Button>
            <Button
              analyticsName="buy-direct-share-pre-approval"
              size="lg"
              variant="ghost"
              onClick={triggerUploadModal}
              aria-label="share-pre-approval-icon"
            >
              <Icon
                size={24}
                name="chevron-right"
                color="neutrals100"
                aria-hidden="true"
                alignSelf="flex-end"
              />
            </Button>
            <InputBase
              ref={hiddenInputRef}
              type="file"
              // https://www.a11yproject.com/posts/2013-01-11-how-to-hide-content/
              border={0}
              height="1px"
              overflow="hidden"
              padding={0}
              position="absolute"
              whiteSpace="nowrap"
              width="1px"
              accept="application/pdf,image/png,image/jpeg,image/jpg"
              onChange={onFileChange}
            />
          </Flex>
          <Text size="110" color="neutrals90" mt={4} mb={6}>
            Upload a file from your phone, tablet, or desktop.
          </Text>
        </Flex>
        <Divider mt={6} mb={6} width="100%" />
        <Text alignSelf="flex-start" mt={6} size="110" color="neutrals90" fontWeight="medium">
          Need to get pre-approved?
        </Text>
        <Flex flexDirection="column" width="100%">
          <Flex flexDirection="row" justifyContent="space-between" alignItems="center" mt={6}>
            <Flex flexDirection="row" alignItems="flex-start" width="100%">
              <Button
                analyticsName="buy-direct-get-pre-approved-text"
                size="lg"
                variant="ghost"
                onClick={handleOpenModal}
                aria-label="get-pre-approved-text"
              >
                <Text size={isMobile ? '160' : '150'} fontWeight="medium">
                  Get pre-approved with{' '}
                </Text>
              </Button>
              <Button
                analyticsName="buy-direct-get-pre-approved-lower-logo"
                size="lg"
                variant="ghost"
                onClick={handleOpenModal}
                aria-label="get-pre-approved-lower-logo"
              >
                <Image
                  alt="Lower.com"
                  height={['24px', '24px', '30px', '30px']}
                  src={'https://imgdrop.imgix.net/05b2b8273fd147e4abb7ec66067d7b2e.png'}
                  objectFit="scale-down"
                />
              </Button>
            </Flex>
            <Button
              analyticsName="buy-direct-get-pre-approved"
              size="lg"
              variant="ghost"
              onClick={handleOpenModal}
              aria-label="get-pre-approved-icon"
            >
              <Icon size={24} name="chevron-right" color="neutrals100" aria-hidden="true" />
            </Button>
          </Flex>
          <Flex width={['90%', null, '80%']}>
            <Text size={isMobile ? '90' : '110'} color="neutrals90" mt={4}>
              See personalized rates that match your budget. And with no lender fees, you’ll save at
              least $1,500 each time you refinance with Lower. Learn more
            </Text>
          </Flex>
        </Flex>
        <Divider mt={8} mb={[2, null, 8]} width="100%" />
        <Flex
          flexDirection={['column', null, 'row-reverse']}
          alignItems="center"
          justifyContent="end"
          mt={[0, null, 8]}
          width="100%"
        >
          <Button
            analyticsName="buy-direct-checkout-pre-approval-review-details"
            aria-label="Review details for contract"
            data-testid="buy-direct-checkout-pre-approval-review-details-button"
            disabled
            mt={8}
            onClick={() => {}}
            variant="primary"
            width={['100%', null, '50%']}
            ml={[null, null, 4]}
          >
            Review details for contract
          </Button>
          <Button
            analyticsName="buy-direct-checkout-pre-approval-back"
            aria-label="Back"
            data-testid="buy-direct-checkout-pre-approval-back-button"
            width={['100%', null, '25%']}
            variant="secondary"
            onClick={onBackClick}
            mt={8}
            mr={[null, null, 4]}
          >
            Back
          </Button>
        </Flex>
      </Flex>
      <LowerMortgageModal
        isOpen={showModal}
        onClick={onLowerPreApprovalClick}
        onClose={handleCloseModal}
      />
    </>
  );
};
